﻿import { Check, Globe } from "lucide-react";
import { useEffect, useRef, useState, useTransition } from "react";

import { SUPPORTED_LOCALES, type Locale } from "@ticketnjoy/types";
import i18n, { useLocale, useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const FLAGS: Record<Locale, string> = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦",
};

/**
 * Custom dropdown — shadcn DropdownMenu henüz ekli değil.
 * Lightweight: outside click + Escape ile kapanan popover.
 *
 * Dil değişince: i18n.changeLanguage() çağırıyoruz; LanguageDetector cookie
 * cache ayarı NEXT_LOCALE'i otomatik yazar, react-i18next subscriber'ları
 * yeniden render eder. <html lang/dir> i18n.ts'teki languageChanged listener
 * tarafından güncellenir. Dolayısıyla burada manuel refresh gerekmiyor.
 */
export function LocaleSwitcher() {
  const locale = useLocale();
  const t = useT("locale");
  const [pending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function handleChange(next: Locale) {
    setOpen(false);
    if (next === locale) return;
    startTransition(() => {
      void i18n.changeLanguage(next);
    });
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={pending}
        aria-label={t("switcher")}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-md border bg-background px-2.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
          pending && "opacity-60",
        )}
      >
        <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="text-base leading-none" aria-hidden>
          {FLAGS[locale]}
        </span>
        <span className="hidden uppercase sm:inline">{locale}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {SUPPORTED_LOCALES.map((code) => {
            const active = code === locale;
            return (
              <button
                key={code}
                role="menuitemradio"
                aria-checked={active}
                type="button"
                onClick={() => handleChange(code)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                  active
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <span className="text-base leading-none" aria-hidden>
                  {FLAGS[code]}
                </span>
                <span className="flex-1 text-start">{t(code)}</span>
                {active && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
